import { createGlobalStyle, css } from "styled-components";
import { hex2RgbComponents } from "./colorTransforms.js";
import { token, versionSuffix } from "./token.js";
const theme = {
  "--white": "#ffffff",
  "--white-rgb": hex2RgbComponents("#ffffff"),
  // Grays
  "--gray1000": "#031026",
  "--gray1000-rgb": hex2RgbComponents("#031026"),
  "--gray900": "#1c283c",
  "--gray800": "#354051",
  "--gray700": "#4f5867",
  "--gray600": "#68707d",
  "--gray500": "#818793",
  "--gray400": "#9a9fa8",
  "--gray300": "#b3b7be",
  "--gray200": "#cdcfd4",
  "--gray100": "#e6e7e9",
  "--gray75": "#edeef0",
  "--gray50": "#f2f3f4",
  "--gray25": "#f8f8f9",
  "--gray10": "#ffffff",
  // System colors
  "--blue1000": "#001f33",
  "--blue900": "#003d66",
  "--blue800": "#005c99",
  "--blue700": "#007acc",
  "--blue600": "#008ae6",
  "--blue500": "#0099ff",
  "--blue500-rgb": hex2RgbComponents("#0099ff"),
  "--blue500a10": `rgb(${token["blue500-rgb"]} / 10%)`,
  "--blue400": "#1aa3ff",
  "--blue300": "#33adff",
  "--blue200": "#80ccff",
  "--blue100": "#ccebff",
  "--red400": "#f45225",
  "--red500": "#f23e0d",
  "--red500-rgb": hex2RgbComponents("#f23e0d"),
  "--red500a10": `rgb(${token["red500-rgb"]} / 10%)`,
  "--red500a50": `rgb(${token["red500-rgb"]} / 50%)`,
  "--red600": "#da380b",
  "--red700": "#c2320a",
  "--yellow500": "#fea900",
  "--yellow500-rgb": hex2RgbComponents("#fea900"),
  "--yellow500a10": `rgb(${token["yellow500-rgb"]} / 10%)`,
  "--yellow600": "#e59800",
  "--yellow700": "#cb8700",
  "--green500": "#72d400",
  "--purple1000": "#15122e",
  "--purple900": "#1f1b44",
  "--purple800": "#2a245c",
  "--purple700": "#40378a",
  "--purple600": "#5549b8",
  "--purple500": "#6b5ce6",
  "--purple400": "#978ced",
  "--purple300": "#c3bdf5",
  "--purple200": "#e1defa",
  "--purple100": "#f7f6fd",
  "--magenta1000": "#2e0d18",
  "--magenta900": "#451424",
  "--magenta800": "#5c1b30",
  "--magenta700": "#8a2949",
  "--magenta600": "#b83761",
  "--magenta500": "#e6457a",
  "--magenta400": "#eb6a94",
  "--magenta300": "#f5b4c9",
  "--magenta200": "#fad9e4",
  "--magenta100": "#fdf5f8",
  "--teal1000": "#062822",
  "--teal900": "#0a3c32",
  "--teal800": "#0d5144",
  "--teal700": "#147a66",
  "--teal600": "#1ba388",
  "--teal500": "#22ccaa",
  "--teal500-rgb": hex2RgbComponents("#22ccaa"),
  "--teal500a10": `rgb(${token["teal500-rgb"]} / 10%)`,
  "--teal400": "#64dbc3",
  "--teal300": "#a6eadd",
  "--teal200": "#d2f4ee",
  "--teal100": "#f3fcfa",
  // Brand colors
  "--brandPrimary700": themableToken("brandPrimary700", "#0087e0"),
  "--brandPrimary600": themableToken("brandPrimary600", "#0095eb"),
  "--brandPrimary500": themableToken("brandPrimary500", "#00aaff"),
  "--brandPrimary500-rgb": themableToken("brandPrimary500-rgb", hex2RgbComponents("#00aaff")),
  "--brandPrimary300": themableToken("brandPrimary300", "#b3e6ff"),
  "--brandPrimary100": themableToken("brandPrimary100", "#e5f7ff"),
  // Spacing
  "--spacing1": "2px",
  "--spacing2": "4px",
  "--spacing3": "8px",
  "--spacing4": "12px",
  "--spacing5": "16px",
  "--spacing6": "24px",
  "--spacing7": "32px",
  "--spacing8": "40px",
  "--spacing9": "48px",
  // Layout
  "--layoutSpacing1": "16px",
  "--layoutSpacing2": "24px",
  "--layoutSpacing3": "32px",
  "--layoutSpacing4": "48px",
  "--layoutSpacing5": "64px",
  "--layoutSpacing6": "96px",
  "--layoutSpacing7": "160px",
  // Border radius
  "--radiusSmall": "2px",
  "--radiusMedium": "5px",
  "--radiusLarge": "8px",
  "--radiusCircle": "50%",
  "--radiusPill": "9999px",
  "--radiusBase": token.radiusMedium,
  "--brandRadiusBase": token.radiusBase,
  // Fonts
  "--fontStackBase": '"Source Sans 3", "Source Sans Pro", SourceSansPro, system-ui, sans-serif',
  "--brandFontStackBase": themableToken("brandFontStackBase", "inherit"),
  "--fontSize25": "10px",
  "--fontSize50": "12px",
  "--fontSize75": "14px",
  "--fontSize100": "16px",
  "--fontSize200": "18px",
  "--fontSize300": "20px",
  "--fontSize400": "22px",
  "--fontSize500": "25px",
  "--fontSize600": "28px",
  "--fontSize700": "32px",
  "--fontSize800": "36px",
  "--fontSize900": "40px",
  "--fontSize1000": "45px",
  "--fontSizeBase": token.fontSize100,
  "--lineHeightBase": "1.25",
  "--lineHeightHeading": "1.25",
  "--lineHeightText": "1.5",
  "--gray900a": `rgb(${token["gray1000-rgb"]} / 90%)`,
  "--gray800a": `rgb(${token["gray1000-rgb"]} / 80%)`,
  "--gray700a": `rgb(${token["gray1000-rgb"]} / 70%)`,
  "--gray600a": `rgb(${token["gray1000-rgb"]} / 60%)`,
  "--gray500a": `rgb(${token["gray1000-rgb"]} / 50%)`,
  "--gray400a": `rgb(${token["gray1000-rgb"]} / 40%)`,
  "--gray300a": `rgb(${token["gray1000-rgb"]} / 30%)`,
  "--gray200a": `rgb(${token["gray1000-rgb"]} / 20%)`,
  "--gray100a": `rgb(${token["gray1000-rgb"]} / 10%)`,
  "--gray75a": `rgb(${token["gray1000-rgb"]} / 7%)`,
  "--gray50a": `rgb(${token["gray1000-rgb"]} / 5%)`,
  "--gray25a": `rgb(${token["gray1000-rgb"]} / 3%)`,
  // Aliases
  "--colorText": token.gray800,
  "--colorTextMuted": token.gray600,
  "--colorTextDisabled": token.gray400,
  // Focus rings
  "--focusRingWidth": "2px",
  "--focusRingColorPrimary": token.blue500,
  "--focusRingColorSecondary": token.gray200,
  // Shadows
  "--colorShadow": token.gray100a,
  "--elevationStatic": `0 0 0 1px ${token.colorShadow}`,
  "--elevation1": `0 1px 4px -1px ${token.colorShadow}`,
  "--elevation2": `0 2px 4px -1px ${token.colorShadow}`,
  "--elevation4": `0 4px 6px -2px ${token.colorShadow}`,
  "--elevation8": `0 8px 12px -2px ${token.colorShadow}`,
  "--elevation12": `0 12px 12px -2px ${token.colorShadow}`,
  "--elevation16": `0 16px 12px -2px ${token.colorShadow}`,
  "--elevation24": `0 24px 12px -2px ${token.colorShadow}`,
  // Button component
  "--componentButtonPrimaryBorderHover": token.brandPrimary600,
  "--componentButtonPrimaryBorderActive": token.brandPrimary700,
  "--componentButtonPrimaryBorderFocus": token.brandPrimary500,
  "--componentButtonPrimaryBorderDisabled": "transparent",
  "--componentButtonPrimaryColor": token.white,
  "--componentButtonPrimaryColorHover": token.white,
  "--componentButtonPrimaryColorActive": token.white,
  "--componentButtonPrimaryColorFocus": token.white,
  "--componentButtonPrimaryColorDisabled": token.white,
  "--componentButtonPrimaryFill": token.brandPrimary500,
  "--componentButtonPrimaryFillHover": token.brandPrimary600,
  "--componentButtonPrimaryFillActive": token.brandPrimary700,
  "--componentButtonPrimaryFillFocus": token.brandPrimary500,
  "--componentButtonPrimaryFillDisabled": token.brandPrimary300,
  "--componentButtonPrimaryFocusRing": `0 0 0 3px rgb(${token["brandPrimary500-rgb"]} / 30%)`,
  "--componentButtonSecondaryBorder": token.gray100,
  "--componentButtonSecondaryBorderHover": token.gray200,
  "--componentButtonSecondaryBorderActive": token.gray300,
  "--componentButtonSecondaryBorderFocus": token.gray300,
  "--componentButtonSecondaryBorderDisabled": token.gray50,
  "--componentButtonSecondaryColor": token.gray800,
  "--componentButtonSecondaryColorHover": token.gray800,
  "--componentButtonSecondaryColorActive": token.gray800,
  "--componentButtonSecondaryColorFocus": token.gray800,
  "--componentButtonSecondaryColorDisabled": token.gray300,
  "--componentButtonSecondaryFill": token.gray25,
  "--componentButtonSecondaryFillHover": token.gray50,
  "--componentButtonSecondaryFillActive": token.gray75,
  "--componentButtonSecondaryFillFocus": token.gray25,
  "--componentButtonSecondaryFillDisabled": token.gray25,
  "--componentButtonSecondaryFocusRing": `0 0 0 3px ${token.gray50a}`,
  "--componentButtonCleanBorderFocus": token.gray200,
  "--componentButtonCleanBorderActive": "transparent",
  "--componentButtonCleanColor": token.gray600,
  "--componentButtonCleanColorHover": token.gray900,
  "--componentButtonCleanColorActive": token.gray900,
  "--componentButtonCleanColorFocus": token.gray900,
  "--componentButtonCleanColorDisabled": token.gray300,
  "--componentButtonCleanFill": "transparent",
  "--componentButtonCleanFillHover": token.gray25a,
  "--componentButtonCleanFillActive": token.gray50a,
  "--componentButtonCleanFillFocus": token.gray25a,
  "--componentButtonCleanFillDisabled": "transparent",
  "--componentButtonCleanFocusRing": `0 0 0 3px ${token.gray50a}`,
  "--componentButtonDangerBorderHover": token.red600,
  "--componentButtonDangerBorderActive": token.red700,
  "--componentButtonDangerBorderFocus": token.red500,
  "--componentButtonDangerBorderDisabled": "transparent",
  "--componentButtonDangerColor": token.white,
  "--componentButtonDangerColorHover": token.white,
  "--componentButtonDangerColorActive": token.white,
  "--componentButtonDangerColorFocus": token.white,
  "--componentButtonDangerColorDisabled": token.white,
  "--componentButtonDangerFill": token.red500,
  "--componentButtonDangerFillHover": token.red600,
  "--componentButtonDangerFillActive": token.red700,
  "--componentButtonDangerFillFocus": token.red500,
  "--componentButtonDangerFillDisabled": token.red500a50,
  "--componentButtonDangerFocusRing": `0 0 0 3px rgb(${token["red500-rgb"]} / 30%)`,
  // Divider
  "--componentDividerColor": token.gray100a,
  // Checkbox
  "--componentCheckboxBorderUnchecked": token.gray300,
  "--componentCheckboxBorderUncheckedHover": token.gray500,
  "--componentCheckboxBorderUncheckedFocus": token.gray500,
  "--componentCheckboxFillDisabled": token.gray100,
  "--componentCheckboxCheckmarkColorDisabled": token.gray500,
  // Radio
  "--componentRadioBorderUnchecked": token.gray300,
  "--componentRadioFillDisabled": token.gray100,
  "--componentRadioCheckmarkColorDisabled": token.gray500,
  "--componentRadioBorderUncheckedHover": token.gray500,
  // Pagination component
  "--componentPaginationBackground": "none",
  "--componentPaginationElevation": `${token.elevationStatic}, ${token.elevation2}`,
  // Tooltip
  "--componentTooltipBackground": token.gray10,
  "--componentTooltipShadow": `${token.elevation2}, ${token.elevationStatic}`,
  "--componentTooltipTitleColor": token.colorText,
  "--componentTooltipSecondaryTextColor": token.colorTextMuted,
  "--componentTooltipTextColor": token.colorTextMuted
};
const darkTheme = {
  "--gray1000": "#ffffff",
  "--gray1000-rgb": hex2RgbComponents("#ffffff"),
  "--gray900": "#e6e7e9",
  "--gray800": "#cdcfd4",
  "--gray700": "#b3b7be",
  "--gray600": "#9a9fa8",
  "--gray500": "#818793",
  "--gray400": "#68707d",
  "--gray300": "#4f5867",
  "--gray200": "#354051",
  "--gray100": "#1c283c",
  "--gray75": "#152135",
  "--gray50": "#101c31",
  "--gray25": "#0b172c",
  "--gray10": "#031026",
  "--componentDividerColor": token.gray100a,
  "--focusRingColorSecondary": token.gray200a,
  // Checkbox
  "--componentCheckboxBorderUnchecked": token.gray300a,
  "--componentCheckboxBorderUncheckedHover": token.gray500a,
  "--componentCheckboxBorderUncheckedFocus": token.gray500a,
  "--componentCheckboxFillDisabled": token.gray100a,
  "--componentCheckboxCheckmarkColorDisabled": token.gray500a,
  // Radio
  "--componentRadioBorderUnchecked": token.gray300a,
  "--componentRadioFillDisabled": token.gray100a,
  "--componentRadioCheckmarkColorDisabled": token.gray500a,
  "--componentRadioBorderUncheckedHover": token.gray500a,
  // Pagination component
  "--componentPaginationBackground": token.gray50a,
  "--componentPaginationElevation": "none",
  // Tooltip
  "--componentTooltipBackground": token.gray100,
  "--componentTooltipShadow": "0 2px 4px -1px rgb(3 16 38 / 10%), 0 0 0 1px rgb(3 16 38 / 10%)",
  // temporarily hardcode values, because there are no shadows defined for a dark theme
  "--componentTooltipTitleColor": token.gray1000,
  "--componentTooltipSecondaryTextColor": token.gray600,
  "--componentTooltipTextColor": token.gray800
};
const lightThemeClass = "ds-light-theme";
const darkThemeClass = "ds-dark-theme";
const ThemeStyles = createGlobalStyle`${css([":root,:host,.", ",.", "{", "}.", "{", "}"], lightThemeClass, darkThemeClass, stringifyTheme(theme), darkThemeClass, stringifyTheme(darkTheme))}`;
function stringifyTheme(themeObject) {
  const propertiesAndValues = Object.entries(themeObject).map(([name, value]) => {
    return `${name}-${versionSuffix}: ${value}`;
  });
  return propertiesAndValues.join(";") + ";";
}
function themableToken(tokenName, defaultValue) {
  return `var(--user-${tokenName}, var(--theme-${tokenName}, ${defaultValue}))`;
}
export {
  ThemeStyles,
  darkThemeClass,
  lightThemeClass,
  theme
};
//# sourceMappingURL=theme.js.map
